import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, CheckCircle, Loader2, ExternalLink } from 'lucide-react';
import { Card, Button } from '@azyntor-labs/nexus-ui';
import { studentGroupsApi } from '@/api/endpoints';
import { toast } from '@/utils/toast';
import { Link } from 'react-router-dom';

interface Props {
  examId: string;
}

interface Group {
  id: string;
  name: string;
  description?: string;
  group_type: string;
  member_count: number;
  subject?: string;
}

export const GroupEnrollment = ({ examId }: Props) => {
  const queryClient = useQueryClient();
  const [enrollingGroupId, setEnrollingGroupId] = useState<string | null>(null);

  // Fetch groups
  const { data: groupsData, isLoading } = useQuery({
    queryKey: ['student-groups'],
    queryFn: async () => {
      const response = await studentGroupsApi.list(true);
      return response.data;
    },
  });

  const groups: Group[] = groupsData?.data || [];

  // Enroll group mutation
  const enrollMutation = useMutation({
    mutationFn: async (groupId: string) => {
      const response = await studentGroupsApi.enrollGroup(groupId, examId);
      return response.data;
    },
    onSuccess: (data) => {
      const result = data.data;
      toast.success(
        `Enrolled ${result.enrolled} students. ${result.already_enrolled} already enrolled.`
      );
      queryClient.invalidateQueries({ queryKey: ['enrollments', examId] });
      setEnrollingGroupId(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to enroll group');
      setEnrollingGroupId(null);
    },
  });

  const handleEnrollGroup = (groupId: string) => {
    setEnrollingGroupId(groupId);
    enrollMutation.mutate(groupId);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Enroll by Student Group</h3>
          <p className="text-sm text-gray-400">Enroll entire cohorts with one click</p>
        </div>
        <Link to="/teacher/groups">
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<ExternalLink className="w-4 h-4" />}
            className="text-cyan-400 hover:text-cyan-300"
          >
            Manage Groups
          </Button>
        </Link>
      </div>

      {/* Groups List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
        </div>
      ) : groups.length === 0 ? (
        <Card className="bg-[#12121a]/80 border-white/[0.06] p-12 text-center">
          <Users className="w-16 h-16 mx-auto text-gray-600 mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No Groups Available</h3>
          <p className="text-gray-400 mb-4">Create student groups to use bulk enrollment</p>
          <Link to="/teacher/groups">
            <Button
              leftIcon={<Users className="w-4 h-4" />}
              className="bg-teal-500 hover:bg-teal-600 text-white"
            >
              Create Your First Group
            </Button>
          </Link>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {groups.map((group) => (
            <Card key={group.id} className="bg-[#12121a]/80 border-white/[0.06] p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-white mb-1">{group.name}</h4>
                  <div className="flex items-center gap-3 text-sm text-gray-400">
                    <span className="capitalize">{group.group_type}</span>
                    {group.subject && (
                      <>
                        <span className="text-white/20">•</span>
                        <span>{group.subject}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {group.description && (
                <p className="text-sm text-gray-400 mb-4 line-clamp-2">{group.description}</p>
              )}

              <div className="flex items-center justify-between pt-4 border-t border-white/[0.06]">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-teal-400" />
                  <span className="text-sm text-white font-medium">{group.member_count} students</span>
                </div>
                <Button
                  onClick={() => handleEnrollGroup(group.id)}
                  disabled={enrollMutation.isPending && enrollingGroupId === group.id}
                  size="sm"
                  leftIcon={
                    enrollMutation.isPending && enrollingGroupId === group.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )
                  }
                  className="bg-teal-500 hover:bg-teal-600 text-white"
                >
                  {enrollMutation.isPending && enrollingGroupId === group.id
                    ? 'Enrolling...'
                    : 'Enroll Group'}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
